import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_VOICE_DURATION_SEC = 120;

/**
 * Candidate containers, in preference order.
 *
 * Critically this does NOT include `audio/webm`, which is what Chrome's
 * MediaRecorder produces by default - the backend's audio allowlist is
 * mpeg / mp4 / aac / ogg, so a webm recording would upload and then be
 * rejected.
 *
 * **The codec is named, not left to the browser.** Asked for a bare
 * `audio/mp4`, Chrome answers "supported" and then records Opus inside the MP4
 * container - and no Apple device can decode Opus, in any container. The note
 * uploaded fine, played fine on the desktop that recorded it, and was silent and
 * flat-waveformed on every iPhone, in Safari and Chrome alike (both WebKit).
 * `mp4a.40.2` is AAC-LC, which every browser and phone plays.
 *
 * The bare types stay as fallbacks after it, for a browser that records mp4 but
 * does not answer the codec-qualified probe. Safari takes `audio/mp4`, Firefox
 * `audio/ogg` - Opus in Ogg, which Apple cannot play either, but Firefox has no
 * AAC encoder to offer and a note that plays on three platforms out of four
 * beats no mic button at all.
 */
const CANDIDATE_MIMES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/aac',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

/**
 * The container this browser can record in, or null when none of the accepted
 * types are supported - in which case the caller should hide the mic button
 * rather than record something that can't be sent.
 */
export const supportedRecordingMime = (): string | null => {
  if (typeof MediaRecorder === 'undefined') return null;
  return (
    CANDIDATE_MIMES.find((mime) => MediaRecorder.isTypeSupported(mime)) ?? null
  );
};

export type VoiceRecorderStatus =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'recorded'
  | 'error';

export const useVoiceRecorder = (
  onComplete: (blob: Blob, mime: string) => void
) => {
  const [status, setStatus] = useState<VoiceRecorderStatus>('idle');
  const [seconds, setSeconds] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const cleanup = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    const mime = supportedRecordingMime();
    if (!mime) {
      setStatus('error');
      return;
    }

    try {
      setStatus('requesting');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      cancelledRef.current = false;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;

      /* What the file is called and uploaded as. The codec parameter is for
         MediaRecorder alone - the backend's allowlist and our own validation
         both match bare container types, and `audio/mp4;codecs=mp4a.40.2`
         matches neither. */
      const containerMime = mime.split(';')[0];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const cancelled = cancelledRef.current;
        const blob = new Blob(chunksRef.current, { type: containerMime });
        cleanup();
        setSeconds(0);
        setStatus('idle');
        if (!cancelled && blob.size > 0) {
          onCompleteRef.current(blob, containerMime);
        }
      };

      recorder.start();
      setSeconds(0);
      setStatus('recording');

      tickRef.current = setInterval(() => {
        setSeconds((current) => {
          const next = current + 1;
          // Hard stop rather than letting someone record a 25MB file they'd
          // only find out was too big after uploading it.
          if (next >= MAX_VOICE_DURATION_SEC) recorderRef.current?.stop();
          return next;
        });
      }, 1000);
    } catch {
      // Permission denied, or no microphone.
      cleanup();
      setStatus('error');
    }
  }, [cleanup]);

  const stop = useCallback(() => {
    cancelledRef.current = false;
    if (recorderRef.current?.state !== 'inactive') recorderRef.current?.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    } else {
      cleanup();
      setSeconds(0);
      setStatus('idle');
    }
  }, [cleanup]);

  return {
    status,
    seconds,
    start,
    stop,
    cancel,
    isRecording: status === 'recording' || status === 'requesting',
  };
};
